import { useState } from 'react';
import { Box, Paper, Typography, Chip, Button, CircularProgress } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DataObjectIcon from '@mui/icons-material/DataObject';
import LanIcon from '@mui/icons-material/Lan';
import VerifiedIcon from '@mui/icons-material/Verified';
import BugReportIcon from '@mui/icons-material/BugReport';
import DownloadIcon from '@mui/icons-material/Download';

const REPORTS = [
  {
    id: 'ssp',
    name: 'System Security Plan (SSP)',
    desc: 'Full system security plan including control implementations, system boundaries, and authorization details.',
    format: 'PDF',
    icon: <ArticleIcon sx={{ fontSize: 32, color: '#00bcd4' }} />,
    lastGenerated: '2026-03-28',
  },
  {
    id: 'stig',
    name: 'STIG Checklist Export',
    desc: 'XCCDF-formatted STIG checklists for all scanned devices, compatible with STIG Viewer.',
    format: 'ZIP',
    icon: <FolderZipIcon sx={{ fontSize: 32, color: '#ff9800' }} />,
    lastGenerated: '2026-04-01',
  },
  {
    id: 'vulns',
    name: 'Vulnerability Report',
    desc: 'Complete vulnerability findings across all assets including severity, CVE details, and remediation status.',
    format: 'XLSX',
    icon: <BugReportIcon sx={{ fontSize: 32, color: '#f44336' }} />,
    lastGenerated: '2026-04-03',
  },
  {
    id: 'poam',
    name: 'POA&M',
    desc: 'Plan of Action & Milestones tracking all open findings, responsible parties, and scheduled completion dates.',
    format: 'XLSX',
    icon: <TableChartIcon sx={{ fontSize: 32, color: '#4caf50' }} />,
    lastGenerated: '2026-03-30',
  },
  {
    id: 'rmf',
    name: 'RMF Controls Summary',
    desc: 'Summary of all NIST SP 800-53 control implementations, test results, and inherited controls.',
    format: 'PDF',
    icon: <VerifiedIcon sx={{ fontSize: 32, color: '#00bcd4' }} />,
    lastGenerated: '2026-03-25',
  },
  {
    id: 'topo',
    name: 'Network Topology Export',
    desc: 'Visual and tabular representation of the network topology including DMZ, VLANs, and device interconnects.',
    format: 'PDF',
    icon: <LanIcon sx={{ fontSize: 32, color: '#a855f7' }} />,
    lastGenerated: '2026-03-20',
  },
  {
    id: 'full',
    name: 'ATO Package (Full)',
    desc: 'Complete authorization package bundle including SSP, POA&M, STIG checklists, and supporting artifacts.',
    format: 'ZIP',
    icon: <FolderZipIcon sx={{ fontSize: 32, color: '#ffeb3b' }} />,
    lastGenerated: '2026-03-28',
  },
  {
    id: 'oscal',
    name: 'OSCAL Export',
    desc: 'Machine-readable Open Security Controls Assessment Language export for automated ATO processing.',
    format: 'JSON',
    icon: <DataObjectIcon sx={{ fontSize: 32, color: '#00bcd4' }} />,
    lastGenerated: null,
  },
];

const FORMAT_COLOR = { PDF: '#f44336', XLSX: '#4caf50', ZIP: '#ff9800', JSON: '#00bcd4' };

export default function AtoExport() {
  const [generating, setGenerating] = useState({});
  const [generated, setGenerated] = useState({});

  const handleGenerate = (id) => {
    setGenerating(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setGenerating(prev => ({ ...prev, [id]: false }));
      setGenerated(prev => ({ ...prev, [id]: new Date().toISOString().slice(0, 10) }));
    }, 1800);
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Header */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>ATO Export</Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 14, marginTop: 0.5 }}>
          Generate authorization packages and compliance reports
        </Typography>
      </Box>

      {/* Cards Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {REPORTS.map(report => {
          const isGenerating = generating[report.id];
          const lastGen = generated[report.id] || report.lastGenerated;

          return (
            <Paper key={report.id} sx={{
              backgroundColor: '#111827',
              border: '1px solid #1e3a5f',
              borderRadius: 2,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {report.icon}
                  <Box>
                    <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15 }}>{report.name}</Typography>
                    <Chip label={report.format} size="small" sx={{
                      backgroundColor: `${FORMAT_COLOR[report.format]}22`,
                      color: FORMAT_COLOR[report.format],
                      fontWeight: 700, fontSize: 10, height: 18, marginTop: 0.25,
                    }} />
                  </Box>
                </Box>
              </Box>

              <Typography sx={{ color: '#7a8fa6', fontSize: 13, lineHeight: 1.6 }}>{report.desc}</Typography>

              {lastGen && (
                <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>
                  Last generated: <span style={{ color: '#7a8fa6' }}>{lastGen}</span>
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, marginTop: 0.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={isGenerating}
                  onClick={() => handleGenerate(report.id)}
                  startIcon={isGenerating ? <CircularProgress size={14} sx={{ color: '#000' }} /> : null}
                  sx={{
                    backgroundColor: '#00bcd4', color: '#000000', fontWeight: 700, fontSize: 12,
                    '&:hover': { backgroundColor: '#00acc1' },
                    '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
                  }}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!lastGen}
                  startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 12,
                    '&:hover': { borderColor: '#00bcd4', color: '#00bcd4' },
                    '&.Mui-disabled': { borderColor: '#1e3a5f', color: '#2a3a50' },
                  }}
                >
                  Download Last
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
